import { lazy, Suspense } from "react";
import { useRoutes } from "react-router-dom";
import PublicRouter from "./PublicRouter";
import LoginV3 from "./Authentication/LoginV3";
import SignupV3 from "./Authentication/SignupV3";
import Profile from "./Profile";

import Users from "./Profile/Users";
import UserProfile from "./Profile/UserProfile";
import Groups from "./Profile/Groups";
import AuditLogs from "./Profile/AuditLogs";
import Security from "./Profile/Security";
import SSOSettings from "./Profile/SSOSettings";
import DesignSystem from "./Profile/DesignSystem";
import Secrets from "./Profile/Secrets";
import { Workflow } from "./Apps/Workflow";

import { Files } from "./Files";
import { Integrations } from "./Integrations/Integrations";
import Docs from "./Docs/index.jsx";
import DocsIntro from "./Docs/components/DocsIntro";
import DeveloperNodeSDK from "./Docs/components/DeveloperNodeSDK";
import DeveloperIntro from "./Docs/components/DeveloperIntro";
import DeveloperAPIReference from "./Docs/components/DeveloperAPIReference";
import DeveloperExpress from "./Docs/components/DeveloperExpress";
import WordDocumentVariables from "./Docs/components/WordDocumentVariables";
import ATPDocs from "./Docs/ATP";
import ATPIntroduction from "./Docs/ATP/components/ATPIntroduction";

import ProtectedRoute from "./ProtectedRoute";
import IntegrationSuccess from "./Integrations/IntegrationSuccess";
const AppBuilder = lazy(() => import("./AppBuilder/AppBuilder"));
import Admin from "./Admin";
import AdminLayout from "./Layouts/AdminLayout";
import ChatMessagesPanel from "./Admin/ChatMessagesPanel";
import ChatDetailPage from "./Admin/ChatDetail";
import ChatImportPage from "./Admin/ChatImport";
import AdminSupportTickets from "./Admin/SupportTickets";
import AdminWorkflowsPanel from "./Admin/AdminWorkflowsPanel";
import AdminCredits from "./Admin/Credits";
import AdminSupportHours from "./Admin/SupportHours";
import BlueprintRequests from "./Admin/BlueprintRequests";
import BlueprintAnalytics from "./Admin/BlueprintAnalytics";
import FacebookAudit from "./Admin/FacebookAudit";
import Intelligence from "./Admin/Intelligence";
import AutomationPlayground from "./Admin/AutomationPlayground";
import AdminAutomationUsage from "./Admin/AutomationUsage";
import DataMigration from "./Admin/DataMigration";
import Databases from "./databases/databases";
import TableRecords from "./databases/TableRecords";
import DatabaseLayout from "./databases/DatabaseLayout";
import Skills from "./skills";
import Blueprints from "./Blueprints";
import BlueprintSharePage from "./Blueprints/BlueprintSharePage";
import Billing from "./Billing";
import Support from "./Support";

import IntegrationError from "./Integrations/IntegrationError";
import IntegrationBuilderPage from "./Integrations/community/IntegrationBuilderPage";
import LayoutWrapper from "./Layouts/LayoutWrapper";

import ChatGPTSkill from "./Docs/components/ChatGPTSkill";
import UsingSkills from "./Docs/components/UsingSkills";
import DatabaseDocs from "./Docs/components/DatabaseDocs";
import WorkflowExecutionMonitoring from "./Monitoring";
import ClientLayoutWrapper from "./Layouts/ClientLayoutWrapper";
import { ClientWorkflows } from "./Client/ClientWorkflows";
import ClientAppIframe from "./Client/ClientAppIframe";
import ChatPage from "./Chat/ChatPage";
import ChatWrapper from "./Chat/ChatWrapper";
import ChatTwoColumnPage from "./Chat/ChatTwoColumnPage/index";
import Onboarding from "./Onboarding";
import TestErrorBoundary from "./components/TestErrorBoundary";
import StatusPage from "./StatusPage";
import SurveyPage from "./Survey/SurveyPage";
import PmfSurveyDashboard from "./Admin/PmfSurvey/PmfSurveyDashboard";
import HarnessReports from "./Admin/HarnessReports/index";

const routes = [
    {
        path: "/",
        element: <LoginV3 />,
    },
    {
        path: "/login/callback",
        element: <LoginV3 />,
    },
    {
        path: "/signup",
        element: <SignupV3 />,
    },
    {
        path: "/signup-secret-token",
        element: <SignupV3 />,
    },
    {
        path: "/shopify-signup",
        element: <SignupV3 isShopifySignup={true} />,
    },
    {
        path: "/form-signup",
        element: <SignupV3 />,
    },
    {
        path: "/v2/",
        element: (
            <ProtectedRoute title="Monitoring">
                <LayoutWrapper />
            </ProtectedRoute>
        ),
        children: [
            {
                path: "monitoring",
                element: <WorkflowExecutionMonitoring />,
            },
        ],
    },
    {
        path: "chat",
        element: <ChatWrapper />,
        children: [
            {
                index: true,
                element: <ChatPage />,
            },
            {
                path: ":workflowId",
                element: (
                    <ProtectedRoute title="Chat">
                        <ChatPage />
                    </ProtectedRoute>
                ),
            },
            {
                path: ":workflowId/chat",
                element: (
                    <ProtectedRoute title="Chat">
                        <ChatTwoColumnPage />
                    </ProtectedRoute>
                ),
            },
        ],
    },
    {
        path: "/client/",
        element: (
            <ProtectedRoute title="Client Workflows">
                <ClientLayoutWrapper />
            </ProtectedRoute>
        ),
        children: [
            {
                path: "workflows",
                element: <ClientWorkflows />,
            },
            {
                index: true,
                element: <ClientWorkflows />,
            },
            {
                path: "app2/:domain",
                element: <ClientAppIframe />,
            },
            {
                path: "workflows/:publicKey",
                element: (
                    <Suspense fallback={null}>
                        <AppBuilder />
                    </Suspense>
                ),
            },
        ],
    },

    {
        path: "workflows",
        element: (
            <ProtectedRoute title="Workflows">
                <Workflow />
            </ProtectedRoute>
        ),
    },
    {
        path: "workflows/apps/:appId",
        element: (
            <ProtectedRoute title="App Workflow">
                <Workflow />
            </ProtectedRoute>
        ),
    },

    {
        path: "databases",
        element: <DatabaseLayout />,
        children: [
            {
                index: true,
                element: (
                    <ProtectedRoute title="Databases">
                        <Databases />
                    </ProtectedRoute>
                ),
            },
            {
                path: ":databaseId/tables/:tableId",
                element: (
                    <ProtectedRoute title="Table Records">
                        <TableRecords />
                    </ProtectedRoute>
                ),
            },
        ],
    },
    {
        path: "/files",
        element: (
            <ProtectedRoute title="Files">
                <Files />
            </ProtectedRoute>
        ),
    },

    {
        path: "/profile",
        element: (
            <ProtectedRoute title="Profile">
                <Profile />
            </ProtectedRoute>
        ),
        children: [
            {
                path: "users",
                element: <Users />,
            },
            {
                path: "groups",
                element: <Groups />,
            },
            {
                path: "audit-logs",
                element: <AuditLogs />,
            },
            {
                path: "security",
                element: <Security />,
            },
            {
                path: "sso",
                element: <SSOSettings />,
            },
            {
                path: "design-system",
                element: <DesignSystem />,
            },
            {
                index: true,
                element: <UserProfile />,
            },
        ],
    },
    {
        path: "/secrets",
        element: (
            <ProtectedRoute title="Secrets">
                <Secrets />
            </ProtectedRoute>
        ),
    },
    {
        path: "/integrate",
        element: (
            <ProtectedRoute title="Connected Apps">
                <Integrations />
            </ProtectedRoute>
        ),
        children: [
            {
                path: "success",
                element: <IntegrationSuccess />,
            },
            {
                path: "error",
                element: <IntegrationError />,
            },
        ],
    },
    {
        path: "/integrate/community",
        element: (
            <ProtectedRoute title="Community Integrations">
                <Integrations />
            </ProtectedRoute>
        ),
    },
    {
        path: "/integrate/build",
        element: (
            <ProtectedRoute title="Integration Builder">
                <IntegrationBuilderPage />
            </ProtectedRoute>
        ),
    },
    {
        path: "/integrate/build/:integrationId",
        element: (
            <ProtectedRoute title="Edit Integration">
                <IntegrationBuilderPage />
            </ProtectedRoute>
        ),
    },
    {
        path: "/docs",
        element: <Docs />,
        children: [
            {
                index: true,
                element: <DocsIntro />,
            },
            {
                path: "/docs/skills/",
                element: <UsingSkills />,
            },
            {
                path: "/docs/skills/databases/",
                element: <DatabaseDocs />,
            },
            {
                path: "/docs/skills/word-variables",
                element: <WordDocumentVariables />,
            },
            {
                path: "/docs/skills/chatgpt-skill",
                element: <ChatGPTSkill />,
            },
            {
                path: "/docs/developer/node-sdk",
                element: <DeveloperNodeSDK />,
            },
            {
                path: "/docs/developer",
                element: <DeveloperIntro />,
            },
            {
                path: "/docs/developer/api-reference",
                element: <DeveloperAPIReference />,
            },
            {
                path: "/docs/developer/express",
                element: <DeveloperExpress />,
            },
        ],
    },
    {
        path: "/docs/atp",
        element: <ATPDocs />,
        children: [
            {
                index: true,
                element: <ATPIntroduction />,
            },
        ],
    },
    {
        path: "/admin",
        element: <Admin />,

        children: [
            {
                index: true,
                element: <Users />,
            },
            {
                path: "chats",
                element: <ChatMessagesPanel />,
            },
            {
                path: "chats/import",
                element: <ChatImportPage />,
            },
            {
                path: "chats/:chatId",
                element: <ChatDetailPage />,
            },
            {
                path: "support",
                element: <AdminSupportTickets />,
            },
            {
                path: "support/:ticketId",
                element: <AdminSupportTickets />,
            },
            {
                path: "all-workflows",
                element: <AdminWorkflowsPanel />,
            },
            {
                path: "credits",
                element: <AdminCredits />,
            },
            {
                path: "support-hours",
                element: <AdminSupportHours />,
            },
            {
                path: "blueprint-requests",
                element: <BlueprintRequests />,
            },
            {
                path: "user-insights",
                element: <BlueprintAnalytics />,
            },
            {
                path: "facebook-audit",
                element: <FacebookAudit />,
            },
            {
                path: "intelligence",
                element: <Intelligence />,
            },
            {
                path: "automation-usage",
                element: <AdminAutomationUsage />,
            },
            {
                path: "automation-playground",
                element: <AutomationPlayground />,
            },
            {
                path: "data-migration",
                element: <DataMigration />,
            },
            {
                path: "pmf-survey",
                element: <PmfSurveyDashboard />,
            },
            {
                path: "harness-reports",
                element: <HarnessReports />,
            },
        ],
    },
    {
        path: "/admin/workflows/:workflowId",
        element: <ChatWrapper Layout={AdminLayout} />,
        children: [
            {
                index: true,
                element: (
                    <ProtectedRoute title="Admin Workflow Editor">
                        <ChatTwoColumnPage />
                    </ProtectedRoute>
                ),
            },
        ],
    },
    {
        path: "/iframe/:publicKey",
        element: (
            <ProtectedRoute title="App Preview">
                <Suspense fallback={null}>
                    <AppBuilder />
                </Suspense>
            </ProtectedRoute>
        ),
    },
    {
        path: "/public/:publicKey",
        element: (
            <Suspense fallback={null}>
                <AppBuilder />
            </Suspense>
        ),
    },

    {
        path: "/skills",
        element: (
            <PublicRouter title="Skills">
                <Skills />
            </PublicRouter>
        ),
    },
    {
        path: "/blueprints",
        element: (
            <PublicRouter title="Blueprints">
                <Blueprints />
            </PublicRouter>
        ),
    },
    {
        path: "/blueprints/share/:shareToken",
        element: (
            <PublicRouter title="Blueprint">
                <BlueprintSharePage />
            </PublicRouter>
        ),
    },
    {
        path: "/billing",
        element: (
            <ProtectedRoute title="Billing">
                <Billing />
            </ProtectedRoute>
        ),
    },
    {
        path: "/support",
        element: (
            <ProtectedRoute title="Support">
                <Support />
            </ProtectedRoute>
        ),
    },
    {
        path: "/support/tickets/:ticketId",
        element: (
            <ProtectedRoute title="Support Ticket">
                <Support />
            </ProtectedRoute>
        ),
    },
    {
        path: "/onboarding",
        element: (
            <ProtectedRoute title="Get Started">
                <Onboarding />
            </ProtectedRoute>
        ),
    },
    {
        path: "/survey/:token",
        element: (
            <PublicRouter title="Survey">
                <SurveyPage />
            </PublicRouter>
        ),
    },
    {
        path: "/status",
        element: (
            <PublicRouter title="System Status">
                <StatusPage />
            </PublicRouter>
        ),
    },
    {
        path: "/test-error",
        element: <TestErrorBoundary />,
    },
];

function RouteComponent() {
    let element = useRoutes(routes);
    return element;
}

export default RouteComponent;
